/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ElevationChart
 * --------------
 * Elevation profile of a route in metres above sea level, on the same window
 * the app's chart uses. Hovering shows a crosshair; `onHover(km | null)` lets
 * the expanded view move a cursor on the map, and `cursorKm` draws the
 * crosshair from outside (route replay) when the pointer is not over it.
 */

import React, { useMemo, useRef, useState } from 'react';
import { elevationProfile, elevationWindow, niceScale } from '../../pages/feed/feedModel';

/** Index of the profile point nearest to a distance in km. */
function nearestIndex(profile, km) {
  let i = 0;
  while (i < profile.length - 1 && profile[i].km < km) i += 1;
  return i;
}

export default function ElevationChart({ route, maxElev, onHover, cursorKm = null, header = true, className = '' }) {
  const profile = useMemo(() => elevationProfile(route), [route]);
  const [hover, setHover] = useState(null);
  const hitRef = useRef(null);
  if (profile.length < 2) return null;
  // Metres above sea level on a window scaled like the app's chart, so the
  // same activity shows the same shape and axis labels on both.
  const { min: lo, max: hi } = elevationWindow(profile.map((p) => p.alt));
  const midAlt = (lo + hi) / 2;
  const totalKm = profile[profile.length - 1].km || 1;
  const W = 480; const H = 130;
  const pts = profile.map((p) => [(p.km / totalKm) * W, H - ((p.alt - lo) / (hi - lo)) * H]);
  const line = `M${pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('L')}`;
  const onMove = (e) => {
    const r = hitRef.current.getBoundingClientRect();
    const fx = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const i = nearestIndex(profile, fx * totalKm);
    setHover({ fx, i });
    if (onHover) onHover(fx * totalKm);
  };
  const onLeave = () => { setHover(null); if (onHover) onHover(null); };
  let cross = hover;
  if (!cross && cursorKm !== null) {
    const fx = Math.min(1, Math.max(0, cursorKm / totalKm));
    cross = { fx, i: nearestIndex(profile, cursorKm) };
  }
  const mid = niceScale(totalKm).max >= 1 ? (totalKm / 2).toFixed(1) : (totalKm / 2).toFixed(2);
  return (
    <div className={`fd-d-sec ${className}`.trim()}>
      {header && <div className="fd-d-sec-h"><span className="fd-eyebrow">Elevation</span>{maxElev !== null && <span>max elev. {Math.round(maxElev)} m</span>}</div>}
      <div className="fd-elev">
        <span className="yl" style={{ top: '4%' }}>{Math.round(hi)} m</span>
        <span className="yl" style={{ top: '50%' }}>{Math.round(midAlt)} m</span>
        <span className="yl" style={{ top: '98%' }}>{Math.round(lo)} m</span>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-label={`Elevation between ${Math.round(lo)} and ${Math.round(hi)} metres above sea level`} role="img">
          <path d={`M0 ${H / 2}H${W}`} stroke="var(--fd-line-soft)" vectorEffect="non-scaling-stroke" />
          <path d={`M0 ${H}H${W}`} stroke="var(--fd-line)" vectorEffect="non-scaling-stroke" />
          <path d={`${line}L${W} ${H}L0 ${H}Z`} fill="var(--fd-accent)" opacity=".12" />
          <path d={line} fill="none" stroke="var(--fd-accent)" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
        <div className="hit" ref={hitRef} onMouseMove={onMove} onMouseLeave={onLeave} />
        {cross && (
          <div className="cross" style={{ left: `${cross.fx * 100}%` }}>
            <i style={{ top: `${(pts[cross.i][1] / H) * 100}%` }} />
            <span>{profile[cross.i].km.toFixed(2)} km · {Math.round(profile[cross.i].alt)} m</span>
          </div>
        )}
      </div>
      <div className="fd-elev-x"><span>0 km</span><span>{mid} km</span><span>{totalKm.toFixed(1)} km</span></div>
    </div>
  );
}
